/**
 * News card excerpt truncation with word boundary detection
 */

interface NewsCard {
  container: HTMLDivElement;
  textElement: HTMLParagraphElement;
  originalText: string;
}

class NewsCardExcerpts {
  private observer: IntersectionObserver;
  private processed = new WeakSet<Element>();
  private cards = new Map<Element, NewsCard>();
  private resizeTimeout?: ReturnType<typeof setTimeout>;

  constructor() {
    this.observer = new IntersectionObserver(
      entries => this.handleIntersection(entries),
      { rootMargin: '50px' }
    );

    this.init();
    this.setupResize();
  }

  private init(): void {
    const containers = document.querySelectorAll('.news-card-excerpt');
    containers.forEach(container => this.observeCard(container));
  }

  public observeNewCards(): void {
    const containers = document.querySelectorAll('.news-card-excerpt');
    containers.forEach(container => {
      if (!this.cards.has(container)) {
        this.observeCard(container);
      }
    });
  }

  private observeCard(container: Element): void {
    const textElement = container.querySelector('p') as HTMLParagraphElement | null;
    if (!textElement) return;

    const card: NewsCard = {
      container: container as HTMLDivElement,
      textElement,
      originalText: textElement.textContent || ''
    };

    this.cards.set(container, card);
    this.observer.observe(container);
  }

  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    entries.forEach(entry => {
      if (entry.isIntersecting && !this.processed.has(entry.target)) {
        const card = this.cards.get(entry.target);
        if (card) {
          this.truncateCard(card);
          this.processed.add(entry.target);
        }
      }
    });
  }

  /**
   * Truncate card text to fit container with word boundary detection
   */
  private truncateCard({
    container,
    textElement,
    originalText,
  }: NewsCard): void {
    // Reset to original text
    textElement.textContent = originalText;

    // Check if text fits
    if (textElement.scrollHeight <= container.clientHeight) return;

    // Binary search for best fit
    let low = 0;
    let high = originalText.length;
    let bestFit = '';

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const testText = originalText.slice(0, mid) + '...';
      textElement.textContent = testText;

      if (textElement.scrollHeight <= container.clientHeight) {
        bestFit = testText;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    // Now find the last complete word in bestFit
    if (bestFit && bestFit.length > 3) {
      // Remove the ellipsis temporarily
      let text = bestFit.slice(0, -3);
      
      // Find the last space
      const lastSpaceIndex = text.lastIndexOf(' ');
      if (lastSpaceIndex > 0) {
        text = text.slice(0, lastSpaceIndex);
      }
      
      // Add ellipsis back
      bestFit = text + '...';
    }

    textElement.textContent = bestFit || originalText.slice(0, 50) + '...';
  }

  private setupResize(): void {
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        // Clear processed set to allow re-truncation
        this.processed = new WeakSet<Element>();
        // Re-truncate visible cards
        this.cards.forEach((card, container) => {
          const rect = container.getBoundingClientRect();
          if (rect.top < window.innerHeight && rect.bottom > 0) {
            this.truncateCard(card);
            this.processed.add(container);
          }
        });
      }, 150);
    });
  }
}

// Singleton instance
let instance: NewsCardExcerpts | null = null;

/**
 * Initialize news card excerpts
 */
export function initNewsCardExcerpts(): void {
  if (!instance) {
    instance = new NewsCardExcerpts();
  }
}

/**
 * Update excerpts for newly added cards (infinite scroll)
 */
export function updateNewsCardExcerpts(): void {
  if (instance) {
    instance.observeNewCards();
  }
}